import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateJobLeadPayload } from "../models/CreateJobLeadPayload/createJobLeadPayload.model";
import { MutateKeys } from "../constants/MutateKeys";
import { filterEmptyValues } from "../utils/GetFilteredPayload";
import { message, notification } from "antd";
import { generatePath } from "react-router-dom";
import { QuarryData } from "../Types";
import { TruckDataPayload } from "../models/TruckDataPayload/truckDataPayload";
import { MaterialTruck } from "../context/materialListingContext";
import { TruckDataListing } from "../models/TruckDetailListing/truckDetailListing.model";
import { jobStatus } from "../Components/CreateJob/CreateJobView/constants";
import { EntityData } from "../models/EntityData/entityData.model";
import { cleanParams } from "../Utilities/queryParamUtils";
import moment from "moment";
import { DateFormat } from "../enums/dateTimeFormat";
import {
  SectionAccessKeys,
  SubSectionAccessKeys,
} from "../enums/AccessPathEnums";
import { isEmptyValue } from "../utils/emptyStringCheck";
import { TruckMaterialJobType } from "../enums/truckMaterialType";

interface LatLngPayload {
  lat?: number;
  lng?: number;
}

interface ContactDetailsProps {
  companyId?: string;
  company?: string;
  lastName?: string;
  notes: string;
  jobId: string[];
  email: string;
  id: string;
  phone: string;
  firstName: string;
  isDriver?: boolean;
  contact_name?: string;
  contact_email?: string;
  contact_number?: string;
}
interface SaveJobLeadPayload {
  companyId?: string;
  company?: string;
  companyName?: string;
  jobType?: string;
  jobName?: string;
  dropOff?: string;
  dropOffLatLng?: string;
  projectInformationSheet?: boolean;
  taxExemptStatus?: boolean;
  salesPerson?: string;
  contactDetails?: ContactDetailsProps;
  jobId?: string;
  notes?: string;
  cloneFromJobId?: string;
  resetOrders?: boolean;
}
interface SaveTruckAndMaterialsDetailsPayload {
  stageId?: string;
  jobId?: string;
  truckAndMaterials?: MaterialTruck | TruckDataListing;
  jobType?: TruckMaterialJobType;
  isDraft?: boolean;
}
interface GetTopQuarriesPayload {
  category?: string;
  subCategory?: string;
  label?: string;
  dropOffLat?: number;
  dropOffLang?: number;
  materialStatus?: string;
  materialSupply?: string;
  materialTest?: string;
  diameter: false;
  quarryId?: string;
  radius?: number;
}

interface AddDocumentPayload {
  assignedTo?: number;
  date?: string;
  docAttachment?: string;
  docAttachmentId?: string;
  docType?: string;
  document?: string;
  fileType?: string;
  leadId?: string;
  sheetStatus?: string;
  signatureVerified?: boolean;
}

interface QuotesPayloadData {
  leadId?: string;
  stageId?: string;
}

interface ApprovalDocumentsSection {
  documentAttachments?: string[];
  status?: string;
  review?: string[];
}

interface ApprovalDocumentData {
  "Project Info Section": ApprovalDocumentsSection;
  "Property Legal Description": ApprovalDocumentsSection;
  "Property Owner Information": ApprovalDocumentsSection;
  "General Contractor Information": ApprovalDocumentsSection;
  "Subcontractor Information": ApprovalDocumentsSection;
  "Bond Information": ApprovalDocumentsSection;
}

interface InternalApprovalPayload {
  jobId?: string;
  dropDown1?: string;
  dropDown2?: string;
  dropDown3?: string;
  name?: string;
  phone?: string;
  email?: string;
  projectBondStatus?: string;
  taxExempt?: string;
  jobSite?: string;
  jobSiteLat?: number;
  jobSiteLang?: number;
  approvalDocuments?: ApprovalDocumentData;
  cashOrCreditProjectDocs?: string[];
  cashOrCreditSale?: boolean;
  stageId?: string;
  projectInformationSheet?: string;
  poInfo1?: string;
  poInfo2?: string;
  poInfo3?: string;
  poInfo4?: string;
}

interface ApprovalPayload {
  status?: string;
  reasonForRejection?: string;
}

interface ApproveInternalDocumentsPayload {
  "Project Info Section": ApprovalPayload;
  "Property Legal Description": ApprovalPayload;
  "Property Owner Information": ApprovalPayload;
  "General Contractor Information": ApprovalPayload;
  "Subcontractor Information": ApprovalPayload;
  "Bond Information": ApprovalPayload;
  reviewedBy?: string;
  reviewedAt?: string;
  leadId?: string;
}

interface DeleteDocumentPayload {
  recordIds?: string[];
  leadId?: string;
}

interface BypassL2ApprovalPayload {
  leadId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

interface FetchListingParamsProps {
  search?: string;
  page?: number;
  perPage?: number;
  jobStatus?: string;
  companyId?: string;
  salesPersonId?: string;
}

interface UpdateMaterialTax {
  quarryId: string;
  materialId: string;
  materialTax: string;
}

interface MaterialFilterParams {
  quarryId?: string;
  category?: string;
  type?: stringl;
}

interface LocationParams {
  lat?: string;
  lon?: string;
}

const urlRegex = "https?://(www.)?[a-zA-Z0-9-]+(.[a-zA-Z]{2,})+(:d+)?(/[^s]*)?";
const CreateJobServices = () => {
  const queryClient = useQueryClient();
  const {
    GET_LEAD_DETAILS,
    FETCH_TICKET_JOBS_META,
    FETCH_CUSTOMER_ACCOUNTS,
    FETCH_CONTACTS,
    FETCH_SALESPERSONS,
    CREATE_JOB,
    SAVE_JOB_LEAD_DRAFT,
    FETCH_DROPDOWN_OPTIONS,
    FETCH_QUARRY_LOCATIONS,
    FETCH_TOP_QUARRY_LOCATIONS,
    FETCH_BIDTOOL_CONFIGURATIONS,
    DROPDOWN_OPTIONS,
    MILES_ROUTE,
    ADD_JOB_DOCUMENTS_ROUTE,
    CREATE_MATERIAL_TRUCK_DETAILS,
    BUILD_QUOTE_GET_ESTIMATES,
    FETCH_BID_CONFIG_INFO,
    BUILD_QUOTE_UPDATE_ESTIMATE,
    ADD_CONTACT,
    EDIT_CONTACT,
    FETCH_ALL_JOBS,
    FETCH_JOB_DETAIL,
    FETCH_INDIVIDUAL_JOB_DETAIL,
    APPROVE_INTERNAL_DOCUMENTS,
    DELETE_DOCUMENTS,
    BYPASS_L2_APPROVAL,
    GET_QUOTE,
    FETCH_ALL_JOBS_MAP,
    ADD_CUSTOMER,
    UPDATE_QUARRY_ROUTE,
    UPDATE_MATERIAL_TAX,
    GET_MATERIAL_CATEGORIES_ROUTE,
    GET_MATERIAL_TYPE_ROUTE,
    GET_MATERIAL_NAME_ROUTE,
    GET_LOCATION_DETAILS_ROUTE,
    FETCH_MATERIAL_VENDOR_OPTIONS,
    FETCH_MATERIAL_OPTION_LIST,
    GET_ALL_JOBS,
  } = ApiRoutes;
  const {
    CREATE_JOB_LEAD,
    SAVE_JOB_LEAD_DRAFT_KEY,
    MILES_KEY,
    GET_TOP_QUARRIES,
    ADD_DOCUMENTS_MUTATE_KEY,
    CREATE_TRUCK_MATERIAL_KEY,
    UPDATE_QUOTE_ESTIMATE,
    ADD_CONTACT_KEY,
    EDIT_CONTACT_KEY,
    SAVE_INTERNAL_APPROVAL,
    APPROVE_INTERNAL_DOCUMENTS_KEY,
    DELETE_DOCUMENTS_KEY,
    APPROVE_DISPATCH_KEY,
    BYPASS_L2_APPROVAL_KEY,
    APPROVE_INTERNAL_COMPLETE_KEY,
    ADD_CUSTOMER_MUTATE_KEY,
    UPDATE_QUARRY_MUTATE_KEY,
    UPDATE_QUARRY_MATERIAL_TAX_KEY,
    GET_JOB_DETAILS_KEY,
    FETCH_LEAD_DETAIL_KEY,
  } = MutateKeys;
  const {
    FETCH_ALL_JOBS_KEY,
    FETCH_CRM_MAP_KEY,
    FETCH_CUSTOMER_ACCOUNT_KEY,
    FETCH_CONTACT_DETAILS_KEY,
    FETCH_DROPDOWN_OPTIONS_KEY,
    FETCH_QUARRY_LOCATIONS_KEY,
    FETCH_BIDTOOL_DATA_KEY,
    FETCH_BID_CONFIG_SETTINGS_KEY,
    FETCH_ESTIMATE_QUOTE_KEY,
    FETCH_DOCUMENTS,
    FETCH_INDIVIDUAL_JOB,
    FETCH_INDIVIDUAL_JOB_DETAIL_KEY,
    FETCH_QUOTE_KEY,
    FETCH_MATERIAL_CATEGORIES_KEY,
    FETCH_MATERIAL_TYPE_KEY,
    FETCH_MATERIAL_NAME_KEY,
    FETCH_LOCATION_DETAILS_KEY,
    GET_MATERIAL_VENDOR_OPTION_LIST,
    GET_MATERIAL_OPTION_LIST,
    FETCH_JOBS_KEY,
  } = QueryKeys;
  const accessParams = {
    section: SectionAccessKeys.CRM,
  };

  const FetchLeadDetail = () => {
    const fetchLeadDetail = async (jobId) => {
      const payload = {
        entityData: {
          jobId,
        },
      };
      const { data } = await axiosInstance.post(GET_LEAD_DETAILS, payload);

      return data;
    };
    return useMutation({
      mutationKey: [FETCH_LEAD_DETAIL_KEY],
      mutationFn: fetchLeadDetail,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchAllJobsMeta = () => {
    const fetchAllJobsMeta = async () => {
      const { data } = await axiosInstance.get(GET_ALL_JOBS);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_JOBS_KEY],
      queryFn: fetchAllJobsMeta,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchCustomerAccounts = () => {
    const fetchCustomerAccounts = async () => {
      const { data } = await axiosInstance.get(FETCH_CUSTOMER_ACCOUNTS, {
        params: accessParams,
      });
      return data ?? [];
    };
    return useQuery({
      queryKey: [FETCH_CUSTOMER_ACCOUNT_KEY],
      queryFn: fetchCustomerAccounts,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };
  const FetchContacts = (companyId: string) => {
    const fetchContacts = async () => {
      const queryParam = {
        companyId: companyId,
      };
      const { data } = await axiosInstance.get(
        `${FETCH_CONTACTS}?companyId=${companyId}`,
        {
          params: {
            ...accessParams,
          },
        },
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_CONTACT_DETAILS_KEY, companyId],
      queryFn: fetchContacts,
      enabled: !isEmptyValue(companyId),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };
  const FetchSalesPerson = () => {
    const fetchSalesPersons = async () => {
      const { data } = await axiosInstance.get(FETCH_SALESPERSONS, {
        params: accessParams,
      });
      return data?.data;
    };
    return useQuery({
      queryKey: [FETCH_SALESPERSONS],
      queryFn: fetchSalesPersons,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };
  const AddDocuments = () => {
    const addDocuments = async ({
      assignedTo,
      date,
      docAttachment,
      docAttachmentId,
      docType,
      document,
      fileType,
      leadId,
      signatureVerified,
      name,
    }: AddDocumentPayload) => {
      const payload = {
        entityData: {
          assignedTo,
          date,
          docAttachment,
          docAttachmentId,
          docType,
          document,
          fileType,
          leadId,
          signatureVerified,
          name,
        },
      };
      const { data } = await axiosInstance.post(
        ADD_JOB_DOCUMENTS_ROUTE,
        payload,
        {
          params: accessParams,
        },
      );
      return data;
    };
    return useMutation({
      mutationKey: [ADD_DOCUMENTS_MUTATE_KEY],
      mutationFn: addDocuments,
    });
  };
  const SaveJobLead = () => {
    const saveJobLead = async ({
      company,
      companyId,
      companyName,
      jobName,
      dropOff,
      dropOffLatLng,
      projectInformationSheet,
      taxExemptStatus,
      salesPerson,
      contactDetails,
      stageId,
      jobId,
      notes,
      cloneFromJobId,
      resetOrders,
    }: SaveJobLeadPayload) => {
      const payload = {
        entityData: {
          companyId: companyId,
          company: company,
          companyName: companyName,
          multipleContacts: [contactDetails],
          leadType: [
            {
              id: "1",
              value: "Material Sales",
            },
          ],
          jobName: jobName,
          dropoff: dropOff,
          dropOffLink: dropOffLatLng.match(urlRegex) ? dropOffLatLng : "",
          dropOffLatLng: {
            lat: !dropOffLatLng.match(urlRegex)
              ? Number(dropOffLatLng?.split(",")?.[0])
              : "",
            lng: !dropOffLatLng.match(urlRegex)
              ? Number(dropOffLatLng?.split(",")?.[1])
              : "",
          },
          stageId,
          isNewCompany: false,
          isNewContact: false,
          projectInformationSheet: projectInformationSheet,
          taxExempt: taxExemptStatus,
          salesPersonId: salesPerson,
          notes: notes,
          cloneFromJobId,
        },
      };
      if (jobId !== "noJobId") {
        const url = generatePath(CREATE_MATERIAL_TRUCK_DETAILS, { jobId });
        const { data } = await axiosInstance.put(
          url,
          {
            ...payload,
            entityData: {
              ...payload.entityData,
              resetOrders,
            },
          },
          {
            params: accessParams,
          },
        );
        return data;
      }
      const { data } = await axiosInstance.post(CREATE_JOB, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [CREATE_JOB_LEAD],
      mutationFn: saveJobLead,
      onSuccess: (data) => {
        queryClient.setQueryData(["createJobDataKey"], data);
      },
    });
  };

  const SaveJobLeadDraft = () => {
    const saveJobLeadDraft = async ({
      company,
      companyId,
      companyName,
      jobName,
      dropOff,
      dropOffLatLng,
      projectInformationSheet,
      taxExemptStatus,
      salesPerson,
      contactDetails,
      jobId,
      notes,
      cloneFromJobId,
      resetOrders,
    }: SaveJobLeadPayload) => {
      const params = {
        isDraft: true,
        companyId: companyId,
        company: company,
        companyName: companyName,
        multipleContacts: !!contactDetails ? [contactDetails] : [],
        leadType: [
          {
            id: "1",
            value: "Material Sales",
          },
        ],
        jobName: jobName,
        dropoff: dropOff,
        dropOffLink: dropOffLatLng.match(urlRegex) ? dropOffLatLng : "",
        dropOffLatLng: !!dropOffLatLng
          ? cleanParams({
              lat: !dropOffLatLng.match(urlRegex)
                ? Number(dropOffLatLng?.split(",")?.[0])
                : "",
              lng: !dropOffLatLng.match(urlRegex)
                ? Number(dropOffLatLng?.split(",")?.[1])
                : "",
            })
          : {
              lat: "",
              lng: "",
            },
        stageId: "Job Lead",
        ...(!!jobId && jobId !== "noJobId" ? { id: jobId } : {}),
        projectInformationSheet: projectInformationSheet,
        taxExempt: taxExemptStatus,
        salesPersonId: salesPerson,
        notes: notes,
        cloneFromJobId,
        resetOrders,
      };
      const cleanedParams = cleanParams(params);
      if (!("dropoff" in cleanedParams)) cleanedParams["dropoff"] = "";
      if (!("notes" in cleanedParams)) cleanedParams["notes"] = "";
      const payload = {
        entityData: {
          ...cleanedParams,
        },
      };
      const { data } = await axiosInstance.post(SAVE_JOB_LEAD_DRAFT, payload, {
        params: accessParams,
      });
      return data;
    };

    return useMutation({
      mutationFn: saveJobLeadDraft,
      mutationKey: [SAVE_JOB_LEAD_DRAFT_KEY],
    });
  };

  const GetDropdownOptions = () => {
    const fetchDropdownOptions = async () => {
      const { data } = await axiosInstance.get(FETCH_DROPDOWN_OPTIONS, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_DROPDOWN_OPTIONS_KEY],
      queryFn: fetchDropdownOptions,
    });
  };
  const GetQuarryLocations = () => {
    const fetchQuarryLocations = async () => {
      const { data } = await axiosInstance.get(FETCH_QUARRY_LOCATIONS, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_QUARRY_LOCATIONS_KEY],
      queryFn: fetchQuarryLocations,
    });
  };

  const GetTopQuarryLocations = () => {
    const fetchTopQuarries = async ({
      category,
      subCategory,
      label,
      materialStatus,
      materialSupply,
      materialTest,
      quarryId,
      radius,
      dropOffLang,
      dropOffLat,
    }: GetTopQuarriesPayload) => {
      const payload = {
        entityData: {
          dropOffLatLng: {
            lat: Number(dropOffLat),
            long: Number(dropOffLang),
          },
          materialStatus: materialStatus,
          materialSupply: materialSupply,
          materialTest: materialTest,
          quarryId: quarryId,
          category: category,
          subCategory: subCategory,
          label: label,
          ...(!!Number(radius) && { radius: Number(radius) }),
        },
      };
      const processedPayload = filterEmptyValues(payload);
      const { data } = await axiosInstance.post(
        FETCH_TOP_QUARRY_LOCATIONS,
        processedPayload,
        {
          params: accessParams,
        },
      );

      return data;
    };
    return useMutation({
      mutationKey: [GET_TOP_QUARRIES],
      mutationFn: fetchTopQuarries,
      onError: () => {
        message.error("Something went wrong");
      },
    });
  };

  const GetMaterialVendorList = () => {
    const getMaterialVendorList = async () => {
      const { data } = await axiosInstance.get(FETCH_MATERIAL_VENDOR_OPTIONS, {
        params: accessParams,
      });
      return data;
    };

    return useQuery({
      queryKey: [GET_MATERIAL_VENDOR_OPTION_LIST],
      queryFn: getMaterialVendorList,
    });
  };

  const GetMaterialOptionList = (vendorId: string) => {
    const params = {
      ...accessParams,
      quarryId: vendorId,
    };
    const cleanedParams = cleanParams(params);
    const getMaterialOptionList = async () => {
      const { data } = await axiosInstance.get(FETCH_MATERIAL_OPTION_LIST, {
        params: cleanedParams,
      });
      return data;
    };

    return useQuery({
      queryKey: [GET_MATERIAL_OPTION_LIST, vendorId],
      queryFn: getMaterialOptionList,
    });
  };

  const GetBidToolsData = () => {
    const fetchBidtoolData = async () => {
      const { data } = await axiosInstance.get(FETCH_BIDTOOL_CONFIGURATIONS, {
        params: accessParams,
      });
      return data;
    };

    return useQuery({
      queryKey: [FETCH_BIDTOOL_DATA_KEY],
      queryFn: fetchBidtoolData,
    });
  };

  const FetchDropdownOptions = () => {
    const fetchDropdownOptions = async () => {
      const { data } = await axiosInstance.get(DROPDOWN_OPTIONS, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_DROPDOWN_OPTIONS_KEY],
      queryFn: fetchDropdownOptions,
    });
  };
  const MilesData = () => {
    const getMilesData = async (payload: { entity_data: { url: string } }) => {
      const { data } = await axiosInstance.post(MILES_ROUTE, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [MILES_KEY],
      mutationFn: getMilesData,
    });
  };
  const SaveTruckAndMaterialsDetails = () => {
    const saveTruckAndMaterial = async ({
      jobId,
      truckAndMaterials,
      stageId,
      jobType,
      isDraft = false,
    }: SaveTruckAndMaterialsDetailsPayload) => {
      const payload = {
        entityData: {
          material: truckAndMaterials,
          stageId,
          ...(isDraft ? { isDraft: true } : {}),
        },
      };
      const url = generatePath(CREATE_MATERIAL_TRUCK_DETAILS, { jobId });
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [CREATE_TRUCK_MATERIAL_KEY],
      mutationFn: saveTruckAndMaterial,
    });
  };
  const FetchSettingsBidConfig = (currentTab?: string) => {
    const getBidConfigInfo = async () => {
      const { data } = await axiosInstance.get(FETCH_BIDTOOL_CONFIGURATIONS, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_BID_CONFIG_SETTINGS_KEY],
      queryFn: getBidConfigInfo,
      refetchOnWindowFocus: false,
      enabled: currentTab === undefined,
    });
  };
  const FetchEstimatesAndQuotes = (payloadData: QuotesPayloadData) => {
    const fetchEstimatesAndQuotesData = async () => {
      const payload = {
        entityData: payloadData,
      };
      const { data } = await axiosInstance.get(
        BUILD_QUOTE_GET_ESTIMATES,
        payload,
        {
          params: accessParams,
        },
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_ESTIMATE_QUOTE_KEY],
      queryFn: fetchEstimatesAndQuotesData,
      enabled: !!payloadData,
    });
  };
  const FetchAllDocuments = (leadId?: string) => {
    const fetchAllDocuments = async () => {
      const url = `${ADD_JOB_DOCUMENTS_ROUTE}?leadId=${leadId}`;
      const { data } = await axiosInstance.get(url, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_DOCUMENTS, leadId],
      queryFn: fetchAllDocuments,
      refetchOnWindowFocus: false,
      enabled: !!leadId,
    });
  };

  const DonwloadEstimate = () => {
    const downloadEstimate = async (payloadData) => {
      const { isDownload, ...payloadValues } = payloadData;
      const payload = {
        entityData: {
          ...payloadValues,
          ...(isDownload ? { downloadUrl: isDownload } : {}),
        },
      };
      const { data } = await axiosInstance.post(
        BUILD_QUOTE_UPDATE_ESTIMATE,
        payload,
        {
          params: accessParams,
        },
      );
      return data;
    };
    return useMutation({
      mutationKey: [UPDATE_QUOTE_ESTIMATE],
      mutationFn: downloadEstimate,
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [FETCH_QUOTE_KEY],
        }),
    });
  };

  const AddContact = () => {
    const addContact = async (payloadData) => {
      const payload = {
        entityData: {
          ...payloadData,
          ...{
            ...(payloadData?.lastName ? {} : { lastName: " " }),
          },
        },
      };
      const { data } = await axiosInstance.post(ADD_CONTACT, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [ADD_CONTACT_KEY],
      mutationFn: addContact,
    });
  };

  const EditContact = () => {
    const editContact = async (payloadData: ContactDetailsProps) => {
      delete payloadData.contact_email;
      delete payloadData.contact_name;
      delete payloadData.contact_number;
      const { companyId, id, ...actualPayload } = payloadData;
      const payload = {
        entityData: {
          ...actualPayload,
        },
      };
      const url = generatePath(EDIT_CONTACT, {
        companyId: companyId,
        id: id,
      });
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [EDIT_CONTACT_KEY],
      mutationFn: editContact,
    });
  };

  const FetchAllJob = (
    params?: FetchListingParamsProps,
    pageSizeChanged: boolean,
    enabled?: boolean,
  ) => {
    const cleanedPayload = params
      ? cleanParams({
          ...params,
          setDefaultPageSize: pageSizeChanged,
        })
      : {};
    const fetchJobs = async () => {
      const { data } = await axiosInstance.post(
        FETCH_ALL_JOBS,
        cleanedPayload,
        {
          params: {
            ...accessParams,
          },
        },
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_ALL_JOBS_KEY],
      queryFn: fetchJobs,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled:
        !!enabled && !!params?.perPage && typeof params?.perPage === "number",
    });
  };

  const FetchAllJobMap = (
    params?: FetchListingParamsProps,
    enabled?: boolean,
  ) => {
    const cleanedPayload = params ? cleanParams(params) : {};
    const fetchJobMapData = async () => {
      const { data } = await axiosInstance.post(
        FETCH_ALL_JOBS_MAP,
        cleanedPayload,
        {
          params: {
            ...accessParams,
          },
        },
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_CRM_MAP_KEY],
      queryFn: fetchJobMapData,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!enabled,
    });
  };

  const GetJobDetails = () => {
    const getJobDetails = async (leadId?: string) => {
      const path = generatePath(FETCH_INDIVIDUAL_JOB_DETAIL, { leadId });
      const { data } = await axiosInstance.get(path);
      return data;
    };
    return useMutation({
      mutationKey: [GET_JOB_DETAILS_KEY],
      mutationFn: getJobDetails,
    });
  };

  const FetchSelectedJob = (leadId?: string) => {
    const fetchJobDetail = async () => {
      const path = generatePath(FETCH_INDIVIDUAL_JOB_DETAIL, { leadId });
      const { data } = await axiosInstance.get(path, {
        params: accessParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_INDIVIDUAL_JOB_DETAIL_KEY, leadId],
      queryFn: fetchJobDetail,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const SaveInternalApproval = () => {
    const saveInternalApproval = async (
      requestPayload?: InternalApprovalPayload,
    ) => {
      const {
        jobId,
        dropDown1,
        dropDown2,
        dropDown3,
        name,
        phone,
        email,
        projectBondStatus,
        taxExempt,
        jobSite,
        jobSiteLat,
        jobSiteLang,
        approvalDocuments,
        cashOrCreditProjectDocs,
        stageId,
        projectInformationSheet,
        poInfo1,
        poInfo2,
        poInfo3,
        poInfo4,
        cashOrCreditSale,
      } = requestPayload;
      const payload = {
        entityData: {
          approvalInfo: {
            name,
            phone,
            email,
            dropDown1,
            dropDown2,
            dropDown3,
            projectBondStatus,
            poInformation: {
              poInfo1,
              poInfo2,
              poInfo3,
              poInfo4,
            },
          },
          taxExempt,
          jobSite,
          jobSiteLatLng: {
            lat: jobSiteLat,
            lng: jobSiteLang,
          },
          approvalDocuments: approvalDocuments,
          cashOrCreditProjectDocs: cashOrCreditProjectDocs,
          stageId: stageId,
          projectInformationSheet: projectInformationSheet,
          cashOrCreditSale: cashOrCreditSale,
        },
      };
      const url = generatePath(CREATE_MATERIAL_TRUCK_DETAILS, { jobId });
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [SAVE_INTERNAL_APPROVAL],
      mutationFn: saveInternalApproval,
    });
  };

  const ApproveInternalDocuments = () => {
    const approveInternalDocuments = async (
      requestPayload?: ApproveInternalDocumentsPayload,
    ) => {
      const { leadId } = requestPayload;
      const url = generatePath(APPROVE_INTERNAL_DOCUMENTS, { leadId });
      const { data } = await axiosInstance.put(
        url,
        {
          entityData: {
            ...requestPayload,
          },
        },
        {
          params: accessParams,
        },
      );
      return data;
    };
    return useMutation({
      mutationKey: [APPROVE_INTERNAL_DOCUMENTS_KEY],
      mutationFn: approveInternalDocuments,
    });
  };

  const DeleteDocuments = () => {
    const deleteDocuments = async (payloadData: DeleteDocumentPayload) => {
      const paylaod = {
        data: {
          entityData: {
            ...payloadData,
          },
        },
        params: accessParams,
      };
      const { data } = await axiosInstance.delete(DELETE_DOCUMENTS, paylaod);
      return data;
    };
    return useMutation({
      mutationKey: [DELETE_DOCUMENTS_KEY],
      mutationFn: deleteDocuments,
    });
  };

  const ApproveDispatch = () => {
    const approveDispatch = async (jobId: string) => {
      const payload = {
        entityData: {
          stageId: jobStatus.dispatchApproved,
        },
      };
      const url = generatePath(CREATE_MATERIAL_TRUCK_DETAILS, { jobId });
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [APPROVE_DISPATCH_KEY],
      mutationFn: approveDispatch,
    });
  };

  const BypassL2Approval = () => {
    const bypassL2Approval = async (payload?: BypassL2ApprovalPayload) => {
      const { data } = await axiosInstance.put(BYPASS_L2_APPROVAL, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [BYPASS_L2_APPROVAL_KEY],
      mutationFn: bypassL2Approval,
    });
  };

  const FetchQuote = (leadId?: string) => {
    const fetchQuote = async () => {
      const path = generatePath(GET_QUOTE, { leadId });
      const { data } = await axiosInstance.get(path, {
        params: accessParams,
      });
      return data?.data ?? null;
    };
    return useQuery({
      queryKey: [FETCH_QUOTE_KEY, leadId],
      queryFn: fetchQuote,
      refetchOnMount: "always",
    });
  };

  const ApproveInternalComplete = () => {
    const approveInternalComplete = async (jobId: string) => {
      const payload = {
        entityData: {
          stageId: jobStatus.internalApprovalComplete,
        },
      };
      const url = generatePath(CREATE_MATERIAL_TRUCK_DETAILS, { jobId });
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationKey: [APPROVE_INTERNAL_COMPLETE_KEY],
      mutationFn: approveInternalComplete,
    });
  };

  const AddCustomer = () => {
    const addCustomer = async (payloadData) => {
      const payload = {
        entityData: payloadData,
      };
      const { data } = await axiosInstance.post(ADD_CUSTOMER, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationFn: addCustomer,
      mutationKey: [ADD_CUSTOMER_MUTATE_KEY],
    });
  };

  const UpdateQuarry = () => {
    const updateQuarry = async (vendorId?: string) => {
      const url = generatePath(UPDATE_QUARRY_ROUTE, {
        id: vendorId,
      });
      const payload = {
        entityData: {
          updatedDate: moment().format(DateFormat.MMM_DD_YYYY),
        },
      };
      const { data } = await axiosInstance.put(url, payload, {
        params: accessParams,
      });
      return data;
    };
    return useMutation({
      mutationFn: updateQuarry,
      mutationKey: [UPDATE_QUARRY_MUTATE_KEY],
    });
  };

  const UpdateQuarryMaterialTax = () => {
    const updateQuarryMaterialTax = async (payloadData?: UpdateMaterialTax) => {
      const { materialId, quarryId, materialTax } = payloadData;
      const payload = {
        entityData: {
          materialId,
          quarryId,
          materialTax,
        },
      };
      const { data } = await axiosInstance.put(UPDATE_MATERIAL_TAX, payload);
      return data;
    };
    return useMutation({
      mutationFn: updateQuarryMaterialTax,
      mutationKey: [UPDATE_QUARRY_MATERIAL_TAX_KEY],
    });
  };

  const GetMaterialCategories = (params?: MaterialFilterParams) => {
    const getMaterialCategories = async () => {
      const { data } = await axiosInstance.get(GET_MATERIAL_CATEGORIES_ROUTE, {
        params: {
          ...params,
          section: "crm",
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_MATERIAL_CATEGORIES_KEY, params],
      queryFn: getMaterialCategories,
      refetchOnWindowFocus: false,
    });
  };

  const GetMaterialType = (params?: MaterialFilterParams) => {
    const getMaterialType = async () => {
      const { data } = await axiosInstance.get(GET_MATERIAL_TYPE_ROUTE, {
        params: {
          ...params,
          section: "crm",
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_MATERIAL_TYPE_KEY, params],
      queryFn: getMaterialType,
      enabled: !!params?.category,
      refetchOnWindowFocus: false,
    });
  };

  const GetMaterialName = (params?: MaterialFilterParams) => {
    const getMaterialName = async () => {
      const { data } = await axiosInstance.get(GET_MATERIAL_NAME_ROUTE, {
        params: {
          ...params,
          section: "crm",
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_MATERIAL_NAME_KEY, params],
      queryFn: getMaterialName,
      enabled: !!params?.category && !!params?.type,
      refetchOnWindowFocus: false,
    });
  };

  const GetLocationFromCoordinates = (params?: LocationParams) => {
    const getLocationFromCoordinates = async () => {
      const { data } = await axiosInstance.get(GET_LOCATION_DETAILS_ROUTE, {
        params: {
          ...params,
          section: "crm",
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_LOCATION_DETAILS_KEY, params],
      queryFn: getLocationFromCoordinates,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!params?.lat && !!params?.lon,
    });
  };

  return {
    FetchLeadDetail,
    FetchAllJobsMeta,
    FetchCustomerAccounts,
    FetchContacts,
    FetchSalesPerson,
    SaveJobLead,
    SaveJobLeadDraft,
    FetchDropdownOptions,
    MilesData,
    GetDropdownOptions,
    GetQuarryLocations,
    GetTopQuarryLocations,
    GetBidToolsData,
    AddDocuments,
    SaveTruckAndMaterialsDetails,
    FetchSettingsBidConfig,
    FetchEstimatesAndQuotes,
    DonwloadEstimate,
    FetchAllDocuments,
    AddContact,
    EditContact,
    FetchAllJob,
    FetchSelectedJob,
    SaveInternalApproval,
    ApproveInternalDocuments,
    DeleteDocuments,
    ApproveDispatch,
    BypassL2Approval,
    FetchQuote,
    ApproveInternalComplete,
    FetchAllJobMap,
    AddCustomer,
    UpdateQuarry,
    UpdateQuarryMaterialTax,
    GetJobDetails,
    GetMaterialCategories,
    GetMaterialType,
    GetMaterialName,
    GetLocationFromCoordinates,
    GetMaterialVendorList,
    GetMaterialOptionList,
  };
};
export default CreateJobServices;
